import { sortBy } from "lodash-es";
import {
  IBooking,
  IDiarySchedule,
  IPouchActions,
  IPouchRowItem,
  IPouchRows,
  ISection,
  PouchDocType,
  ISectionLookupCache
} from "shared-types/index";
import PouchLoadService from "external-services/pouch-load-service/index";
import { ILoginActions, IVenueActions, VenueActionNames } from "shared-types/ActionsTypes";

interface IDefaultSectionLayout {
  sectionId: string
  layoutId:  string
}

class SectionLookupService {


  private sectionsLookUps: ISectionLookupCache | boolean = false;
  private trackEvent?: (eventName: any, values?: any) => void;

  setup(trackEvent?: (eventName: any, values?: any) => void) {
    this.trackEvent = trackEvent;
  }


  init(LoginActions: ILoginActions, VenueActions: IVenueActions, PouchActions: IPouchActions) {
    PouchActions.handlePouchSyncedDoctype(PouchDocType.section, () => this.clearCache())
    LoginActions.handleLogout(() => this.clearCache());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.clearCache());
  }

  setSectionLookUps(val: ISectionLookupCache | boolean): void {
    this.sectionsLookUps = val;
  }

  getSectionLookUps(): ISectionLookupCache | boolean {
    return this.sectionsLookUps;
  }

  clearCache(): void {
    this.sectionsLookUps = false;
  }

  /**
   * Returns all the sections
   * @return {Promise}
   */
  getSections(): Promise<ISection[]> {
    return Promise.resolve(PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      attachments: false,
      startkey: 'section_',
      endkey: 'section_\uffff'
    }).then((sections: unknown) => {
      return (sections as IPouchRows<ISection>).rows.map((item: IPouchRowItem<ISection>) => {
        return item.doc;
      });
    }));
  }


  /**
   * Returns all the sections
   * @return {Promise of type ISectionLookup}
   * @todo: make it return ISectionLookup[] when converting to TS. severity: medium
   */
  getSectionsLookUp(): Promise<ISectionLookupCache | boolean> {
    const self = this;
    // if the sections are in memory return them without a new look up
    if (this.sectionsLookUps) {
      return new Promise((resolve) => {
        resolve(this.sectionsLookUps);
      });
    }
    return Promise.resolve(PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      attachments: false,
      startkey: 'section_',
      endkey: 'section_\uffff'
    }).then((sections: unknown) => {
      if (!this.sectionsLookUps) {
        this.sectionsLookUps = {};
      }
      let sectionLookUps: any = {};
      (sections as IPouchRows<ISection>).rows.reduceRight((_, sectionRow: IPouchRowItem<ISection>) => {
        const section = sectionRow.doc;
        sectionLookUps[section._id] = section;
        return null;
      }, 0);
      sectionLookUps = this.manageDuplicateOrder(sectionLookUps);
      this.sectionsLookUps = sectionLookUps;
      return sectionLookUps;
    }));
  }


  /**
   * Returns full section docs given an array of ids
   * @param {Array} An Array of sections
   * @return {Promise}
   */
  getSectionsByIds(ids: string[]): Promise<ISection[]> {
    return Promise.resolve(PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      attachments: false,
      keys: ids,
    }).then((layouts: unknown) => {
      return (layouts as IPouchRows<ISection>).rows.map((item: IPouchRowItem<ISection>) => {
        return item.doc;
      });
    }));
  }
  getSectionById(id: string): Promise<ISection> {
    return Promise.resolve(PouchLoadService.getDbLocal().get(id) as unknown as ISection);
  }
  addSection(name: string): Promise<boolean> {
    return Promise.resolve(this.getSections().then((sections: ISection[]) => {
      // get the last section and make the order one more
      sections = sortBy(sections, (section) => section.order);
      const order = sections[sections.length - 1].order + 1;
      const section = {
        name: name,
        order: order,
        type: 'section',
        _id: PouchLoadService.getNewId(['section'], true)
      };
      this.trackEvent && this.trackEvent('New Sections', {
        id: section._id
      });
      return PouchLoadService.getDbLocal().put(section).then(() => {
        this.sectionsLookUps = false;
        return true;
      });
    }));
  }

  saveSections(sections: ISection[]): Promise<boolean> {
    this.trackEvent && this.trackEvent('Save Sections');
    return Promise.resolve(PouchLoadService.getDbLocal().bulkDocs(sections).then(() => {
      this.sectionsLookUps = false;
      return true;
    }));
  }

  /**
   * In some cases 'order' seems to be duplicating (bad data), so here we try and fix that using alpha-numeric sorting
   */
  manageDuplicateOrder(sectionsLookup: {[key: string]: ISection}): {
    [key: string]: ISection
  } {
    let arr: { propName: string, section: { order: number, name: string } }[] = [];
    let duplicateOrderDetected = false;

    // loops through the existing section lookup object and creates array that we can reliably sort
    for (const propName in sectionsLookup) {
      const sectionLU: ISection = sectionsLookup[propName];
      const thisOrder = sectionLU.order;
      arr.push({
        propName,
        section: sectionLU
      });

      if (arr.find(({section}) => section.order === thisOrder)) {
        duplicateOrderDetected = true;
      }
    }

    // if no duplicates detected, returns the same object that was passed in with no modifications
    if (!duplicateOrderDetected) {
      return sectionsLookup;
    }

    // first sort by name (alpha-numeric) and then by order
    arr = sortBy(arr, ({section}) => section.name);
    arr = sortBy(arr, ({section}) => section.order);

    // recreate the lookup object
    const newLookup: any = {};
    arr.forEach(({section, propName}, i) => {
      section.order = i;
      newLookup[propName] = section;
    });
    return newLookup;
  }



  /**
   * In some cases 'order' seems to be duplicating (bad data), so here we try and fix that by incrementing the order of the duplicated data
   */
  setOrderForSection(sections: ISection[]): ISection[] {
    for (let i = 0; i < sections.length; i++) {
      for (let j = i + 1; j < sections.length; j++) {
        if (sections[i].order === sections[j].order) {
          sections[j].order = sections[j].order + 1;
        }
      }
    }
    return sections;
  }


  checkIfSectionExists(booking: IBooking, sectionId: string): boolean {
    return booking.tables.some(t => t.sectionId === sectionId); // For normal booking have to include the booking table present in another section
  }


  getDefaultSectionLayouts(schedule: IDiarySchedule): {defaultSectionLayouts: IDefaultSectionLayout[], sectionIds: string[] } {
    const sectionsByLayout = new Map()
    const sectionIdsWithDuplicates = schedule?.services.reduce((result, service) => {
      const ids = service.sectionLayouts ? service.sectionLayouts.map(({ sectionId, layoutId }) => {
        // check to make sure every section is either closed or uses the open service
        if (!sectionsByLayout.has(sectionId) || !sectionsByLayout.get(sectionId)) {
          // a layout for this section has not been defined or is set to null
          sectionsByLayout.set(sectionId, layoutId)
        }
        return sectionId
      }) : [];
      return [...result, ...ids]
    }, [])

    const defaultSectionLayouts: IDefaultSectionLayout[] = []
    sectionsByLayout.forEach((layoutId, sectionId) => {
      defaultSectionLayouts.push({ sectionId, layoutId })
    })
    return {
      sectionIds: Array.from(new Set(sectionIdsWithDuplicates)), // Unique id
      defaultSectionLayouts
    }
  }

}

const instance = new SectionLookupService();
export default instance as SectionLookupService;
